import { useEffect, useMemo, useState } from 'react'
import type { Navigate } from '../App'
import { useActiveIslands, useSettings, useStore } from '../state/store'
import { languageOf } from '../lib/languages'
import { levelFor } from '../lib/levels'
import { generateContent, generationStatus, type Allowance } from '../lib/api'
import {
  checkIslandDraft,
  checkScenarioDraft,
  materialiseIsland,
  materialiseScenario,
  MAX_BEATS,
  MAX_BLOCKS,
  MIN_BEATS,
  MIN_BLOCKS,
  type IslandDraft,
  type ScenarioDraft,
} from '../lib/contentDraft'
import { SpeakButton } from '../components/SpeakButton'

/**
 * Describe an island; Claude drafts it; you read it before it becomes yours.
 *
 * The review step is the whole design. Generated language content is confidently
 * wrong in a way that only shows up months later, when you have drilled a phrase
 * no one says — so nothing is saved until someone who can judge it has looked at
 * it. The server has already rejected anything that fails mechanically; what is
 * left is the judgement a checker cannot make.
 */
export function CreateWithAI({ navigate }: { navigate: Navigate }) {
  const { data, dispatch } = useStore()
  const settings = useSettings()
  const islands = useActiveIslands()
  const lang = languageOf(settings.activeLanguage)
  const token = data.sync.token

  const [kind, setKind] = useState<'island' | 'scenario'>('island')
  const [brief, setBrief] = useState('')
  const [register, setRegister] = useState('however people really speak')
  const [count, setCount] = useState(5)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [allowance, setAllowance] = useState<Allowance | null>(null)
  const [configured, setConfigured] = useState<boolean | null>(null)

  const [draft, setDraft] = useState<IslandDraft | ScenarioDraft | null>(null)
  const [draftKind, setDraftKind] = useState<'island' | 'scenario'>('island')
  const [saved, setSaved] = useState<string | null>(null)

  /**
   * The islands a scenario may point at, in the order the server will number
   * them. Sent with the request and used again when the draft comes back, so
   * beat 1's "island 3" means the same island in both directions.
   */
  const catalogue = useMemo(() => islands.slice(0, 12), [islands])

  useEffect(() => {
    if (!token) return
    void generationStatus(token).then((result) => {
      if (!result.ok) return
      setConfigured(result.configured)
      setAllowance(result.allowance)
    })
  }, [token])

  const limits = kind === 'island' ? { min: MIN_BLOCKS, max: MAX_BLOCKS } : { min: MIN_BEATS, max: MAX_BEATS }
  const canSubmit = Boolean(token) && brief.trim().length >= 3 && !busy

  async function submit() {
    if (!token || !canSubmit) return
    setBusy(true)
    setError(null)
    setDraft(null)
    setSaved(null)

    const asked = kind
    const result = await generateContent(token, {
      kind: asked,
      brief: brief.trim(),
      register: register.trim(),
      count: Math.min(limits.max, Math.max(limits.min, count)),
      level: levelFor(settings, settings.activeLanguage),
      ...(asked === 'scenario' ? { islands: catalogue } : {}),
    })

    setBusy(false)
    if (!result.ok) {
      setError(result.error)
      return
    }

    // Checked again here, with the same code the server used. The server is not
    // the authority in this app — this device is — and a draft that arrives
    // malformed must never reach the store, where it would sync everywhere and
    // take the app down with it.
    const check =
      asked === 'island'
        ? checkIslandDraft(result.draft, lang.code)
        : checkScenarioDraft(result.draft, lang.code, catalogue)

    if (!check.ok) {
      setError(
        `That draft did not pass the checks on this device: ${check.problems
          .map((p) => `${p.where} — ${p.problem}`)
          .join('; ')}`,
      )
      return
    }

    setAllowance(result.allowance)
    setDraftKind(asked)
    setDraft(check.value)
  }

  function save() {
    if (!draft) return
    if (draftKind === 'island') {
      const island = materialiseIsland(draft as IslandDraft, lang.code)
      dispatch({ type: 'upsertIsland', island })
      setSaved(island.id)
    } else {
      const scenario = materialiseScenario(draft as ScenarioDraft, lang.code, catalogue)
      dispatch({ type: 'upsertScenario', scenario })
      setSaved(scenario.id)
    }
    setDraft(null)
  }

  if (!token) {
    return (
      <>
        {/* Every other screen that is not the dashboard offers this, and this one
            did not — so the only way out of the builder was the rail, or the
            browser's back button on an app that has no history to go back to. */}
        <button
          type="button"
          className="btn is-quiet is-small"
          onClick={() => navigate({ view: 'islands' })}
          style={{ marginBottom: '0.8rem' }}
        >
          ← Islands
        </button>

        <header className="page-head">
          <h1>Build with AI</h1>
        </header>
        <div className="card">
          <p className="muted">
            Generating content needs an account, since it runs on the server rather than in your
            browser.
          </p>
          <button type="button" className="btn is-primary" onClick={() => navigate({ view: 'signin' })}>
            Sign in
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Every other screen that is not the dashboard offers this, and this one
          did not — so the only way out of the builder was the rail, or the
          browser's back button on an app that has no history to go back to. */}
      <button
        type="button"
        className="btn is-quiet is-small"
        onClick={() => navigate({ view: 'islands' })}
        style={{ marginBottom: '0.8rem' }}
      >
        ← Islands
      </button>

      <header className="page-head">
        <h1>Build with AI</h1>
        <p className="muted">
          Describe what you need to be able to say. You will see the draft before anything is saved.
        </p>
      </header>

      {configured === false && (
        <div className="notice is-error" style={{ marginBottom: '1rem' }}>
          Generation is switched off: this site has no AI key of its own, and there is none on
          your account either. Add one in Settings — Anthropic, OpenAI, or Google all work.
        </div>
      )}

      {error && (
        <div className="notice is-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {saved && (
        <div className="notice" style={{ marginBottom: '1rem' }}>
          Saved to your {lang.name} content.{' '}
          <button
            type="button"
            className="btn is-small"
            onClick={() =>
              navigate(draftKind === 'island' ? { view: 'island', id: saved } : { view: 'scenarios' })
            }
          >
            Open it
          </button>
        </div>
      )}

      {!draft && (
        <div className="card">
          <div className="field">
            <span className="field-label">What are you making?</span>
            <div className="row" style={{ gap: '0.5rem' }}>
              <button
                type="button"
                className={`btn ${kind === 'island' ? 'is-primary' : ''}`}
                onClick={() => setKind('island')}
              >
                An island
              </button>
              <button
                type="button"
                className={`btn ${kind === 'scenario' ? 'is-primary' : ''}`}
                onClick={() => setKind('scenario')}
                disabled={catalogue.length === 0}
                title={
                  catalogue.length === 0
                    ? 'A scenario is built from islands you already have.'
                    : undefined
                }
              >
                A scenario
              </button>
            </div>
          </div>

          <label className="field">
            <span className="field-label">
              {kind === 'island'
                ? 'What do you need to be able to say?'
                : 'What situation should this put you in?'}
            </span>
            <textarea
              className="textarea"
              rows={4}
              value={brief}
              placeholder={
                kind === 'island'
                  ? 'Renting a flat — asking about the deposit, what is included, when I can move in.'
                  : 'A landlord showing me a flat. They talk fast and want an answer today.'
              }
              onChange={(e) => setBrief(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">How should it sound?</span>
            <input
              className="input"
              value={register}
              placeholder="polite but not formal"
              onChange={(e) => setRegister(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">
              {kind === 'island' ? 'Lines' : 'Beats'} — {count}
            </span>
            <input
              type="range"
              min={limits.min}
              max={limits.max}
              step={1}
              value={Math.min(limits.max, Math.max(limits.min, count))}
              onChange={(e) => setCount(Number(e.target.value))}
            />
          </label>

          {kind === 'scenario' && (
            <p className="muted" style={{ fontSize: '0.86rem' }}>
              Built from {catalogue.length} of your {lang.name} islands, so every beat is answerable
              with something you already drill.
            </p>
          )}

          <div className="row" style={{ marginTop: '0.8rem', alignItems: 'center' }}>
            <button
              type="button"
              className="btn is-primary"
              onClick={() => void submit()}
              disabled={!canSubmit || configured === false}
            >
              {busy ? 'Writing it…' : 'Generate'}
            </button>
            {busy && (
              <span className="muted" style={{ fontSize: '0.86rem' }}>
                This takes a few seconds — it is checked and rewritten if it does not pass.
              </span>
            )}
            {allowance && !busy && (
              <span className="muted" style={{ fontSize: '0.86rem', marginLeft: 'auto' }}>
                {allowance.remaining} of {allowance.limit} left today
              </span>
            )}
          </div>
        </div>
      )}

      {draft && draftKind === 'island' && (
        <IslandPreview
          draft={draft as IslandDraft}
          language={lang.code}
          onSave={save}
          onDiscard={() => setDraft(null)}
        />
      )}

      {draft && draftKind === 'scenario' && (
        <ScenarioPreview
          draft={draft as ScenarioDraft}
          language={lang.code}
          onSave={save}
          onDiscard={() => setDraft(null)}
        />
      )}
    </>
  )
}

function IslandPreview({
  draft,
  language,
  onSave,
  onDiscard,
}: {
  draft: IslandDraft
  language: string
  onSave: () => void
  onDiscard: () => void
}) {
  const lang = languageOf(language)
  return (
    <div className="card appear">
      <div className="card-head">
        <div>
          <h2 style={{ margin: 0 }}>{draft.title}</h2>
          <div className={lang.code === 'zh' ? 'hanzi' : ''}>{draft.titleZh}</div>
        </div>
        <span className="stat-label">draft</span>
      </div>

      <p className="muted">{draft.useWhen}</p>

      <div className="stack">
        {draft.blocks.map((block, i) => (
          <div key={i} className="row" style={{ justifyContent: 'space-between', gap: '1rem' }}>
            <div>
              <div className={lang.code === 'zh' ? 'hanzi' : ''} style={{ fontSize: '1.1rem' }}>
                {block.hanzi}
              </div>
              {lang.hasPhonetics && <div className="pinyin">{block.pinyin}</div>}
              <div className="muted" style={{ fontSize: '0.9rem' }}>
                {block.english}
              </div>
              {block.note && (
                <div className="muted" style={{ fontSize: '0.84rem' }}>
                  {block.note}
                </div>
              )}
            </div>
            <SpeakButton text={block.hanzi} language={lang.code} withSlow />
          </div>
        ))}
      </div>

      <Decision onSave={onSave} onDiscard={onDiscard} noun="island" />
    </div>
  )
}

function ScenarioPreview({
  draft,
  language,
  onSave,
  onDiscard,
}: {
  draft: ScenarioDraft
  language: string
  onSave: () => void
  onDiscard: () => void
}) {
  const lang = languageOf(language)
  return (
    <div className="card appear">
      <div className="card-head">
        <div>
          <h2 style={{ margin: 0 }}>{draft.title}</h2>
          <div className={lang.code === 'zh' ? 'hanzi' : ''}>{draft.setting}</div>
        </div>
        <span className="stat-label">draft</span>
      </div>

      <p className="muted">{draft.brief}</p>

      <div className="stack">
        {draft.beats.map((beat, i) => (
          <div key={i} className="row" style={{ justifyContent: 'space-between', gap: '1rem' }}>
            <div>
              <div className="stat-label">{beat.speaker}</div>
              <div className={lang.code === 'zh' ? 'hanzi' : ''} style={{ fontSize: '1.05rem' }}>
                {beat.promptHanzi}
              </div>
              {lang.hasPhonetics && <div className="pinyin">{beat.promptPinyin}</div>}
              <div className="muted" style={{ fontSize: '0.9rem' }}>
                {beat.promptEnglish}
              </div>
              <div style={{ fontSize: '0.9rem' }}>→ {beat.cue}</div>
            </div>
            <SpeakButton text={beat.promptHanzi} language={lang.code} withSlow />
          </div>
        ))}
      </div>

      <Decision onSave={onSave} onDiscard={onDiscard} noun="scenario" />
    </div>
  )
}

function Decision({
  onSave,
  onDiscard,
  noun,
}: {
  onSave: () => void
  onDiscard: () => void
  noun: string
}) {
  return (
    <>
      <div className="divider" />
      <div className="row" style={{ alignItems: 'center' }}>
        <button type="button" className="btn is-primary" onClick={onSave}>
          Save this {noun}
        </button>
        <button type="button" className="btn" onClick={onDiscard}>
          Start over
        </button>
        <span className="muted" style={{ fontSize: '0.84rem', marginLeft: 'auto' }}>
          Read it first — you can edit every line after saving.
        </span>
      </div>
    </>
  )
}
